import { computed, inject, Injectable, signal } from '@angular/core';
import { finalize, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AnomalyLog, DiaryLogStatus, EventLogField, LoaderService, Patient, PatientDiaryLog, PatientEventLogsFilter, SearchResult, SearchSortOptions, TspDiaryRepository, UpdateAnomalyStatus } from '../core';
import { LogItem } from '../models';
import { SortEvent } from '../shared';
import { BaseService } from './base.service';
import { PatientService } from './patient.service';
import { UserService } from './user.service';


/**
 * This service handles patient history page data.
*/
@Injectable({ providedIn: 'root' })
export class DiaryService extends BaseService {

	private readonly userSrv = inject(UserService);
	private readonly patSrv = inject(PatientService);
	private readonly loaderSrv = inject(LoaderService);
	private readonly diaryRepo = inject(TspDiaryRepository);

	patient = signal<Patient | null>(null);
	isReadonly = signal<boolean>(true);

	lastSync = signal<Date | undefined>(undefined);

	readonly DEFAULT_ANOMALY_FILTERS: PatientEventLogsFilter = {
		pinSiteCare: true,
		strutsAdj: true,
		prescrSync: true,
		other: false,
		page: 1,
		top: 10,
		sortOrder: {
			name: EventLogField.anomalyDate,
			desc: true
		}
	};
	anomalyFilters = signal<PatientEventLogsFilter>({ ...this.DEFAULT_ANOMALY_FILTERS });
	anomalySortOrder = computed<SortEvent>(() => this._sortEventMapper(this.anomalyFilters().sortOrder!));
	anomalies = signal<LogItem[]>([]);
	anomalyCount = signal<number>(0);

	readonly DEFAULT_DIARY_FILTERS: PatientEventLogsFilter = {
		pinSiteCare: true,
		strutsAdj: true,
		prescrSync: true,
		other: true,
		page: 1,
		top: 10,
		sortOrder: {
			name: EventLogField.effectiveDate,
			desc: true
		}
	};
	diaryFilters = signal<PatientEventLogsFilter>({ ...this.DEFAULT_DIARY_FILTERS });
	diarySortOrder = computed<SortEvent>(() => this._sortEventMapper(this.diaryFilters().sortOrder!));
	diaries = signal<LogItem[]>([]);
	diaryCount = signal<number>(0);



	/**
	 * Init patient history data.
	 */
	init(patientId: string): Observable<boolean> {
		this.loaderSrv.show();
		return this.patSrv.getPatient(patientId).pipe(
			switchMap(pat => !pat ? throwError(() => new Error('Patient not exists')) : of(pat)),
			tap(pat => this.patient.set(pat)),
			switchMap(pat => forkJoin([
				this._getLastSync(pat.userGuid, pat.id),
				this._getAnomalies(pat.userGuid, pat.id, this.DEFAULT_ANOMALY_FILTERS),
				this._getDiaries(pat.userGuid, pat.id, this.DEFAULT_DIARY_FILTERS),
				this.userSrv.isCustomerCareOtherUser(pat.userGuid),
			])),
			tap(([lastSync, anomalyRes, diaryRes, isReadonly]) => {
				this.lastSync.set(lastSync ? new Date(lastSync) : lastSync);
				this._initAnomalies(anomalyRes);
				this._initDiaries(diaryRes);
				this.isReadonly.set(isReadonly);
			}),
			map(() => true),
			finalize(() => this.loaderSrv.hide())
		);
	}

	private _initAnomalies(result: SearchResult<AnomalyLog> | null): void {
		this.anomalies.set(result?.items.map(i => this._anomalyLogItemMapper(i)) ?? []);
		this.anomalyCount.set(result?.count ?? 0);
	}

	private _initDiaries(result: SearchResult<PatientDiaryLog> | null): void {
		this.diaries.set(result?.items.map(i => this._diaryLogItemMapper(i)) ?? []);
		this.diaryCount.set(result?.count ?? 0);
	}

	private _anomalyLogItemMapper(anomaly: AnomalyLog): LogItem {
		return {
			id: anomaly.anomalyLogId,
			scheduledDate: new Date(anomaly.date),
			type: anomaly.type,
			message: anomaly.subtype,
			status: DiaryLogStatus.Problem,
			frameID: anomaly.frameId,
			isRead: anomaly.isRead,
			isSelected: false
		};
	}

	private _diaryLogItemMapper(diary: PatientDiaryLog): LogItem {
		return {
			effectiveDate: new Date(diary.effectiveDate),
			scheduledDate: diary.expectedDate ? new Date(diary.expectedDate) : undefined,
			type: diary.type,
			status: diary.status,
			message: diary.subtype,
			frameID: diary.frameId
		};
	}

	private _sortEventMapper(order: SearchSortOptions): SortEvent {
		return {
			column: order.name,
			direction: order.desc ? 'desc' : (order.desc == false ? 'asc' : '')
		};
	}

	/**
	 * Get last sync date.
	 */
	private _getLastSync(userId: string, patientId: string): Observable<Date | undefined> {
		return this.diaryRepo.patientLastSync(userId, patientId).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Get anomaly logs.
	 */
	private _getAnomalies(userId: string, patientId: string, filters: PatientEventLogsFilter): Observable<SearchResult<AnomalyLog>> {
		return this.diaryRepo.anomalyLogList(userId, patientId, filters).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Get diary log.
	 */
	private _getDiaries(userId: string, patientId: string, filters: PatientEventLogsFilter): Observable<SearchResult<PatientDiaryLog>> {
		return this.diaryRepo.diaryLogList(userId, patientId, filters).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Search anomaly logs.
	 */
	searchAnomalies(): Observable<boolean> {
		this.loaderSrv.show();
		return this._getAnomalies(this.patient()!.userGuid, this.patient()!.id, this.anomalyFilters()).pipe(
			tap(res => this._initAnomalies(res)),
			finalize(() => this.loaderSrv.hide()),
			map(() => true)
		);
	}

	/**
	 * Search diary logs.
	 */
	searchDiaries(): Observable<boolean> {
		this.loaderSrv.show();
		return this._getDiaries(this.patient()!.userGuid, this.patient()!.id, this.diaryFilters()).pipe(
			tap(res => this._initDiaries(res)),
			finalize(() => this.loaderSrv.hide()),
			map(() => true)
		);
	}

	/**
	 * Change read/unread status of anomaly logs.
	 */
	changeReadStatus(items: UpdateAnomalyStatus[]): Observable<boolean> {
		this.loaderSrv.show();
		return this.diaryRepo.anomalyReadStatus(items).pipe(
			map(res => this._handleApiResponse(res)),
			finalize(() => this.loaderSrv.hide()),
			map(() => true)
		);
	}

	/**
	 * Reset patient history data.
	 */
	reset(): void {
		this.patient.set(null);
		this.isReadonly.set(true);
		this.lastSync.set(undefined);
		this.anomalyFilters.set({ ...this.DEFAULT_ANOMALY_FILTERS });
		this.anomalies.set([]);
		this.anomalyCount.set(0);
		this.diaryFilters.set({ ...this.DEFAULT_DIARY_FILTERS });
		this.diaries.set([]);
		this.diaryCount.set(0);
	}
}